// src/components/dashboard/DeliveryManagement.jsx
import { useEffect, useState } from "react";
import DeliveryService from "../../services/dashboard/DeliveryService";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit, MapPin, Eye, EyeOff, ChevronDown, ChevronUp } from "react-feather";

export default function DeliveryManagement() {
  const [boys, setBoys] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // create/edit form state
  const [showCreate, setShowCreate] = useState(false);
  const [editingBoy, setEditingBoy] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [password, setPassword] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // assign modal
  const [assigningTo, setAssigningTo] = useState(null); // boy object
  const [selectedPinIds, setSelectedPinIds] = useState(new Set());
  
  // password visibility in list
  const [visiblePasswords, setVisiblePasswords] = useState(new Set());
  
  // expanded areas state
  const [expandedAreas, setExpandedAreas] = useState(new Set());

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const boysRes = await DeliveryService.getAllBoys();
      const pinsRes = await DeliveryService.getAllPincodes();
      // backend may return wrapper (responseData) or raw array — try both
      const boysList =
        boysRes?.responseData ??
        boysRes?.data ??
        (Array.isArray(boysRes) ? boysRes : []);
      const pinsList =
        pinsRes?.responseData ??
        pinsRes?.data ??
        (Array.isArray(pinsRes) ? pinsRes : []);
      setBoys(boysList);
      setPincodes(pinsList);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load delivery data");
    } finally {
      setLoading(false);
    }
  };

  const createBoy = async (ev) => {
    ev?.preventDefault();
    
    if (!password || password.trim().length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    
    try {
      const payload = {
        name,
        phone,
        email,
        vehicleDetails: vehicle,
        password: password.trim(),
        isActive: true,
      };

      await DeliveryService.createBoy(payload, photoFile);
      toast.success("Delivery partner created");
      resetForm();
      await loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.responseMessage || "Failed to create delivery partner");
    }
  };

  const updateBoy = async (ev) => {
    ev?.preventDefault();
    
    if (!editingBoy) return;
    
    try {
      const boyId = editingBoy.deliveryBoyId ?? editingBoy.id ?? editingBoy.delivery_boy_id;
      const payload = {
        name,
        phone,
        email,
        vehicleDetails: vehicle,
        isActive: true,
      };

      // Only include password if provided
      if (password && password.trim()) {
        payload.password = password.trim();
      }

      await DeliveryService.updateBoy(boyId, payload, photoFile);
      toast.success("Delivery partner updated");
      resetForm();
      await loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.responseMessage || "Failed to update delivery partner");
    }
  };

  const resetForm = () => {
    setShowCreate(false);
    setEditingBoy(null);
    setName("");
    setPhone("");
    setEmail("");
    setVehicle("");
    setPassword("");
    setPhotoFile(null);
    setShowPassword(false);
  };

  const openEdit = (boy) => {
    setEditingBoy(boy);
    setName(boy.name || "");
    setPhone(boy.phone || boy.mobileNumber || "");
    setEmail(boy.email || boy.emailId || "");
    setVehicle(boy.vehicleDetails || "");
    setPassword("");
    setPhotoFile(null);
    setShowCreate(true);
  };

  const onDelete = async (id) => {
    const ok = window.confirm("Delete this delivery partner?");
    if (!ok) return;
    try {
      await DeliveryService.deleteBoy(id);
      toast.success("Deleted");
      await loadAll();
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  };

  const openAssign = (boy) => {
    setAssigningTo(boy);
    // preselect pins if API returns them (boy.areas, boy.pincodes, or boy.assignedPincodes)
    const assigned = (boy.areas || boy.pincodes || boy.assignedPincodes || [])
      .map((p) => p.pinId ?? p.id ?? p.pin_id)
      .filter(Boolean);
    setSelectedPinIds(new Set(assigned));
  };

  const togglePasswordVisibility = (boyId) => {
    const newVisible = new Set(visiblePasswords);
    if (newVisible.has(boyId)) {
      newVisible.delete(boyId);
    } else {
      newVisible.add(boyId);
    }
    setVisiblePasswords(newVisible);
  };
  
  const toggleAreaExpansion = (boyId) => {
    const newExpanded = new Set(expandedAreas);
    if (newExpanded.has(boyId)) {
      newExpanded.delete(boyId);
    } else {
      newExpanded.add(boyId);
    }
    setExpandedAreas(newExpanded);
  };

  const selectAllPincodes = () => {
    const allIds = pincodes.map((p) => p.pinId ?? p.id ?? p.pin_id).filter(Boolean);
    setSelectedPinIds(new Set(allIds));
  };

  const deselectAllPincodes = () => {
    setSelectedPinIds(new Set());
  };

  const togglePin = (id) => {
    const s = new Set(selectedPinIds);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelectedPinIds(s);
  };

  const submitAssign = async () => {
    if (!assigningTo) return;
    try {
      const arr = Array.from(selectedPinIds);
      const boyId =
        assigningTo.deliveryBoyId ??
        assigningTo.id ??
        assigningTo.delivery_boy_id;
      await DeliveryService.assignPincodesToBoy(boyId, arr);
      toast.success("Pincodes assigned");
      setAssigningTo(null);
      setSelectedPinIds(new Set());
      await loadAll();
    } catch (e) {
      console.error(e);
      toast.error("Assign failed");
    }
  };

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.5);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(75, 85, 99, 0.7);
        }
      `}</style>
      <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Delivery Management
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage delivery partners and operational pincodes
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 font-medium text-[15px]"
          >
            <Plus size={18} /> Add Delivery Partner
          </button>
        </header>

      {showCreate && (
        <form
          onSubmit={editingBoy ? updateBoy : createBoy}
          className="bg-white dark:bg-gray-900/60 p-5 rounded-xl border border-gray-200/60 dark:border-gray-800/80 shadow-sm"
        >
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            {editingBoy ? "Edit Delivery Partner" : "Add New Delivery Partner"}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              required
              placeholder="Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            <input
              required
              placeholder="Phone *"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            <input
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            <input
              placeholder="Vehicle Details"
              value={vehicle}
              onChange={(e) => setVehicle(e.target.value)}
              className="p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>

          {/* Password Field */}
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password {!editingBoy && <span className="text-red-500">*</span>}
              {editingBoy && <span className="text-gray-500 dark:text-gray-400 text-xs">(leave blank to keep current)</span>}
            </label>
            <div className="relative max-w-md">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={editingBoy ? "Enter new password (optional)" : "Enter password *"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border dark:border-gray-600 rounded pr-10 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                required={!editingBoy}
                minLength={4}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {!editingBoy && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Minimum 4 characters required</p>
            )}
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Photo (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
              className="text-sm dark:text-gray-300"
            />
          </div>
          
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded hover:bg-orange-600 transition"
            >
              {editingBoy ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Delivery Boys List - Card-like Rows */}
      <div className="space-y-3">
        {/* Header */}
        <div className="grid grid-cols-12 gap-4 px-4 py-2">
          <div className="col-span-3">
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
              Delivery Partner
            </span>
          </div>
          <div className="col-span-2">
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
              Password
            </span>
          </div>
          <div className="col-span-4">
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
              Assigned Areas
            </span>
          </div>
          <div className="col-span-3">
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
              Actions
            </span>
          </div>
        </div>

        {loading ? (
          <div className="bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80 p-12 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        ) : boys.length === 0 ? (
          <div className="bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80 p-16 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
                <MapPin className="w-8 h-8 text-gray-400 dark:text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">No delivery partners yet</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Add your first delivery partner to get started</p>
              </div>
              <button
                onClick={() => setShowCreate(true)}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 text-white dark:text-gray-900 transition-all duration-200 text-sm font-medium"
              >
                <Plus size={16} /> Add Delivery Partner
              </button>
            </div>
          </div>
        ) : (
          boys.map((b) => {
            const key = b.deliveryBoyId ?? b.id ?? b.delivery_boy_id;
            const isPasswordVisible = visiblePasswords.has(key);
            const isAreaExpanded = expandedAreas.has(key);
            const areas = b.areas || [];
            const areaCount = areas.length;

            return (
              <div
                key={key}
                className="group bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="grid grid-cols-12 gap-4 px-4 py-4 items-center">
                  {/* User Info - Name, Email, Phone */}
                  <div className="col-span-3">
                    <div className="space-y-1">
                      <div className="text-[15px] font-semibold text-gray-950 dark:text-gray-50">
                        {b.name ?? `${b.firstName ?? ""} ${b.lastName ?? ""}`}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {b.email ?? b.emailId ?? "No email"}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        {b.phone ?? b.mobileNumber}
                      </div>
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="col-span-2">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200/60 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200">
                      <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                        {isPasswordVisible ? (b.password || "****") : "••••••••"}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(key)}
                        className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all duration-150 hover:scale-110"
                        title={isPasswordVisible ? "Hide password" : "Show password"}
                      >
                        {isPasswordVisible ? (
                          <EyeOff size={12} className="text-gray-500 dark:text-gray-400" />
                        ) : (
                          <Eye size={12} className="text-gray-500 dark:text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Areas - Collapsed/Expanded */}
                  <div className="col-span-4">
                    {areaCount === 0 ? (
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">No areas assigned</span>
                    ) : (
                      <div className="space-y-2">
                        {/* Collapsed Preview - Enhanced Affordance */}
                        <button
                          onClick={() => toggleAreaExpansion(key)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg border border-gray-200/60 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 hover:shadow-sm"
                        >
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {areaCount} {areaCount === 1 ? "area" : "areas"} assigned
                          </span>
                          {isAreaExpanded ? (
                            <ChevronUp size={14} className="text-gray-500 transition-transform duration-200" />
                          ) : (
                            <ChevronDown size={14} className="text-gray-500 transition-transform duration-200" />
                          )}
                        </button>

                        {/* Expanded List */}
                        {isAreaExpanded && (
                          <div className="flex flex-wrap gap-1.5 pt-1 animate-in fade-in duration-200">
                            {areas.map((a) => {
                              const id = a.pinId ?? a.id ?? a.pin_id;
                              const label = a.pinCode
                                ? `${a.cityName ? `${a.cityName} - ` : ""}${a.pinCode}`
                                : `${a.city?.cityName ?? a.city ?? ""} - ${a.pin_code ?? ""}`;

                              return (
                                <span
                                  key={id}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 border border-gray-200/60 dark:border-gray-700/60"
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions - Enhanced Hierarchy */}
                  <div className="col-span-3">
                    <div className="inline-flex items-center gap-1 p-1 bg-gray-50/50 dark:bg-gray-800/30 rounded-lg border border-gray-200/40 dark:border-gray-700/40 group-hover:border-gray-300/60 dark:group-hover:border-gray-600/60 transition-all duration-200">
                      <button
                        title="Assign Pincodes"
                        onClick={() => openAssign(b)}
                        className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-500 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 transition-all duration-200 hover:scale-110"
                      >
                        <MapPin size={16} />
                      </button>
                      <button
                        title="Edit"
                        onClick={() => openEdit(b)}
                        className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-200 transition-all duration-200 hover:scale-110"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => onDelete(key)}
                        className="p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-500 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-all duration-200 hover:scale-110"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      </div>

      {/* Assign Pincodes Modal - Enhanced */}
      {assigningTo && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 p-4">
          <div className="bg-white dark:bg-gray-900/95 w-full max-w-3xl p-6 rounded-xl shadow-2xl border border-gray-200/60 dark:border-gray-800/80 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-5">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                  Assign Pincodes
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {assigningTo.name || assigningTo.email}
                </p>
              </div>
              <button
                onClick={() => {
                  setAssigningTo(null);
                  setSelectedPinIds(new Set());
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-150"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Select All / Deselect All */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200/60 dark:border-gray-800/80">
              <button
                onClick={selectAllPincodes}
                className="px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all duration-200 font-medium hover:scale-105"
              >
                Select All
              </button>
              <button
                onClick={deselectAllPincodes}
                className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 font-medium hover:scale-105"
              >
                Deselect All
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-auto font-medium">
                {selectedPinIds.size} of {pincodes.length} selected
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-auto mb-5 pr-2 custom-scrollbar">
              {pincodes.map((pin) => {
                const id = pin.pinId ?? pin.id ?? pin.pin_id;
                const label =
                  pin.pinCode ??
                  pin.pin_code ??
                  `${pin.cityName ?? pin.city}-${
                    pin.pinCode ?? pin.pin_code ?? ""
                  }`;
                return (
                  <label
                    key={id}
                    className="flex items-center gap-3 p-3 border border-gray-200/60 dark:border-gray-700/60 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPinIds.has(id)}
                      onChange={() => togglePin(id)}
                      className="rounded w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {pin.cityName ?? pin.city ?? ""}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200/60 dark:border-gray-800/80">
              <button
                onClick={() => {
                  setAssigningTo(null);
                  setSelectedPinIds(new Set());
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={submitAssign}
                className="px-5 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 text-white dark:text-gray-900 rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
